/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 91 P1-9 — pure RTDB read/write helpers for the new simplified
// SafeCircle "Static" layer. Lives alongside the existing complex
// SafeCircle architecture (profiles + modes + circles) without
// touching it; this layer is what the redesigned bottom-tab UI shows
// in Build 91. Phase 2 will fold dynamic proximity sorting in on top
// of this same record shape (the `dynamicEnabled` + `staticOrder`
// fields are reserved now so we don't owe a migration later).

import {
  type Database,
  push,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import {
  addReaderIndexEntry,
  clearReaderIndexEntry,
} from "./safeCircleReaders";

export interface SafeCircleStaticMember {
  /** RTDB push id for this entry. Stable across edits. */
  memberId: string;
  /** Server timestamp (epoch ms) when the member was added. */
  addedAt: number;
  /** Display name shown in the avatar row + remove dialog. */
  displayName: string;
  /** E.164 phone number ("+15555550100"). Required so the Phase 2
   *  SMS fan-out can reach the member without re-prompting. */
  phoneNumber: string;
  /** Set when the member was picked from the user's Friends list —
   *  the friend's MyAmbits uid is stored so notifications can route
   *  in-app once the dynamic layer ships. Empty for phone-contact
   *  picks. */
  linkedFriendUid?: string;
  /** Optional relationship label ("Mom", "Friend", custom) the user
   *  picks at Add time and that surfaces as the pill on the detail
   *  screen. Added in Build 93 — older records simply have it absent. */
  relation?: string;
  /** Phase 2 will turn this on per-member to opt into proximity
   *  sorting. Default false in Phase 1 — UI stays static. */
  dynamicEnabled: boolean;
  /** Sequential 0..N-1 that drives the Phase 1 avatar-row order. */
  staticOrder: number;
}

// Build 93 — orbit ring fits 6 nodes (5 members + Add slot, or 6
// members with Add docked below). Lowered from 8 in Build 91; any
// pre-existing 7- or 8-member circles simply render in the ring (the
// view caps by index, not by deleting persisted rows).
const HARD_CAP = 6;

export const SAFE_CIRCLE_HARD_CAP = HARD_CAP;

interface AddMemberInput {
  displayName: string;
  phoneNumber: string;
  linkedFriendUid?: string;
  relation?: string;
}

function memberCollectionPath(uid: string): string {
  return `users/${uid}/safeCircle`;
}

function memberPath(uid: string, memberId: string): string {
  return `${memberCollectionPath(uid)}/${memberId}`;
}

/**
 * Persist a new SafeCircle member. The caller is expected to enforce
 * the hard cap (we still reject here as a server-side guard against
 * race conditions; UI should rarely hit this). `existingCount` is
 * passed in so we don't issue another RTDB read on the write path —
 * the subscriber already knows the count.
 */
export async function addSafeCircleMember(
  database: Database,
  uid: string,
  input: AddMemberInput,
  existingCount: number,
): Promise<SafeCircleStaticMember> {
  if (!uid) throw new Error("safecircle/add: no uid");
  if (existingCount >= HARD_CAP) {
    throw new Error("safecircle/add: hard cap reached");
  }
  const collectionRef = ref(database, memberCollectionPath(uid));
  const newRef = push(collectionRef);
  const memberId = newRef.key;
  if (!memberId) throw new Error("safecircle/add: push returned no key");

  const trimmedRelation = input.relation?.trim();
  const record = {
    memberId,
    addedAt: serverTimestamp(),
    displayName: input.displayName.trim(),
    phoneNumber: input.phoneNumber.trim(),
    linkedFriendUid: input.linkedFriendUid ?? null,
    relation: trimmedRelation && trimmedRelation.length > 0 ? trimmedRelation : null,
    dynamicEnabled: false,
    staticOrder: existingCount,
  };
  await set(newRef, record);

  // Mirror in-app friends into the UID-keyed reader index so the live-
  // activity RTDB rule can grant this friend read access. Phone-only members
  // (no linkedFriendUid) are correctly skipped by addReaderIndexEntry.
  await addReaderIndexEntry(database, uid, input.linkedFriendUid ?? undefined);

  return {
    memberId,
    addedAt: Date.now(),
    displayName: record.displayName,
    phoneNumber: record.phoneNumber,
    linkedFriendUid: record.linkedFriendUid ?? undefined,
    relation: record.relation ?? undefined,
    dynamicEnabled: false,
    staticOrder: existingCount,
  };
}

/**
 * Remove a member by id. We do NOT re-pack staticOrder on the
 * remaining members — gaps are fine in Phase 1 because the display
 * sort can use array-index after sorting by staticOrder. Phase 2's
 * dynamic layer ignores staticOrder anyway.
 */
export async function removeSafeCircleMember(
  database: Database,
  uid: string,
  memberId: string,
  membersBeforeRemoval?: SafeCircleStaticMember[],
): Promise<void> {
  if (!uid || !memberId) throw new Error("safecircle/remove: missing args");
  await remove(ref(database, memberPath(uid, memberId)));
  // Revoke the reader-index entry for this member's linked friend, but only
  // if no other remaining member still links the same uid. Callers that have
  // the member list on hand (the subscriber hook does) pass it so we never
  // issue an extra read here. When omitted, the index is left untouched and
  // will be reconciled by the next backfill.
  if (membersBeforeRemoval) {
    await clearReaderIndexEntry(database, uid, membersBeforeRemoval, memberId);
  }
}

/**
 * Parse a raw RTDB snapshot value into the typed shape. Tolerates
 * missing fields (defaults dynamicEnabled to false, staticOrder to
 * Number.MAX_SAFE_INTEGER so unknown-order entries sort to the
 * bottom). Returns `null` when the entry is malformed beyond repair.
 */
export function parseSafeCircleMember(
  memberId: string,
  raw: unknown,
): SafeCircleStaticMember | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.displayName !== "string" || typeof r.phoneNumber !== "string") {
    return null;
  }
  return {
    memberId,
    addedAt: typeof r.addedAt === "number" ? r.addedAt : 0,
    displayName: r.displayName,
    phoneNumber: r.phoneNumber,
    linkedFriendUid:
      typeof r.linkedFriendUid === "string" && r.linkedFriendUid.length > 0
        ? r.linkedFriendUid
        : undefined,
    relation:
      typeof r.relation === "string" && r.relation.length > 0
        ? r.relation
        : undefined,
    dynamicEnabled: r.dynamicEnabled === true,
    staticOrder:
      typeof r.staticOrder === "number" ? r.staticOrder : Number.MAX_SAFE_INTEGER,
  };
}

/**
 * Stable sort: by staticOrder asc, then by addedAt asc as the tie
 * breaker. Returns a new array; never mutates the input.
 */
export function sortSafeCircleMembers(
  members: SafeCircleStaticMember[],
): SafeCircleStaticMember[] {
  return [...members].sort((a, b) => {
    if (a.staticOrder !== b.staticOrder) return a.staticOrder - b.staticOrder;
    return a.addedAt - b.addedAt;
  });
}

// Re-exported for tests so the touch-up paths can target a known
// constant rather than re-deriving the RTDB shape.
export const __testing = { memberCollectionPath, memberPath };

/**
 * One-shot rewrite of a member's staticOrder. Exported for the
 * (future, Phase 2) reorder UI; not used by the Phase 1 view but
 * available so callers don't need to re-import update from
 * firebase/database. Kept here so the helper module owns every path
 * that mutates /users/{uid}/safeCircle/{memberId}.
 */
export async function updateMemberOrder(
  database: Database,
  uid: string,
  memberId: string,
  staticOrder: number,
): Promise<void> {
  await update(ref(database, memberPath(uid, memberId)), { staticOrder });
}
